import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";

const STORAGE_KEY = "masar_lang";
const LanguageContext = createContext(null);

function lookup(dict, path) {
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved && translations[saved] ? saved : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const t = useCallback(
    (path) => {
      const val = lookup(translations[lang] || translations.en, path);
      if (val !== undefined && val !== null) return val;
      const fallback = lookup(translations.en, path);
      return fallback !== undefined && fallback !== null ? fallback : path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, isRtl: lang === "ar" }), [lang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}
